import axiosAuth from '@/services/axios_auth'
import axiosPlain from 'axios'
import sha256 from 'crypto-js/sha256'
import Base64 from 'crypto-js/enc-base64'
import {getReponseType, getRequestType, typeGuard} from '@/types/simmerring-helper.d'
import {IS_WIDGET, WIDGET_CONFIG} from '~/widgets/shared/widget-config'

// Widget auth helper - computes hash for widget authentication
function getWidgetAuthHeaders(companyNo: string, selectorName: string, sourceKey: string) {
  const companySelector = `${companyNo}_${selectorName}`
  const timestamp = new Date(Date.now())
  const newMinutes = ((Math.floor(timestamp.getMinutes() / 5) * 5) + 5)
  if (newMinutes >= 60) {
    timestamp.setHours(timestamp.getHours() + 1)
  }
  timestamp.setMinutes(newMinutes % 60)
  timestamp.setSeconds(0)
  timestamp.setMilliseconds(0)
  const timestampString = timestamp.toISOString()

  const hashInput = companySelector + sourceKey + timestampString
  const hash = Base64.stringify(sha256(hashInput))

  return {
    'Fst-Custom-Company': companySelector,
    'Fst-Custom-Source': hash,
    'Fst-Custom-Timestamp': timestampString
  }
}

const simmerringMetadataResponseType = getReponseType(
  '/1.0.0/api/SimmerringFinder',
  'get',
  {
    query: {
      apiKey: ''
    }
  }
)

const simmerringFinderResponseType = getReponseType(
  '/1.0.0/api/SimmerringFinder',
  'post',
  {
    query: {
      apiKey: ''
    }
  }
)

const simmerringFinderRequestType = getRequestType(
  '/1.0.0/api/SimmerringFinder',
  'post',
  {
    query: {
      apiKey: ''
    }
  }
)

const simmerringAssistantResponseType = getReponseType(
  '/1.0.0/api/SimmerringAssistant',
  'post',
  {
    query: {
      apiKey: ''
    }
  }
)

const simmerringAssistantRequestType = getRequestType(
  '/1.0.0/api/SimmerringAssistant',
  'post',
  {
    query: {
      apiKey: ''
    }
  }
)

type SimmerringFinderResponse = typeof simmerringFinderResponseType;
type SimmerringFinderRequest = typeof simmerringFinderRequestType;
type SimmerringAssistantResponse = typeof simmerringAssistantResponseType;
type SimmerringAssistantRequest = typeof simmerringAssistantRequestType;
type SimmerringMetadata = typeof simmerringMetadataResponseType;

export const useSimmerringSelectorService = () => {

  const isWidget = inject(IS_WIDGET, false)
  const widgetConfig = isWidget ? inject(WIDGET_CONFIG) : null

  const runtimeConfig = isWidget ? null : useRuntimeConfig()
  const baseUrl = widgetConfig?.apiBaseUrl ?? runtimeConfig?.public.BASIC_API_URL ?? 'https://apim-enext-stg.azure-api.net'
  const simmerringApiKey = isWidget
    ? (widgetConfig?.simmerringApiKey || '92049B89-0669-4B7B-ABF4-FD26CAAD98A6')
    : runtimeConfig?.public.SIMMERRING_API_KEY
  const sourceKey = isWidget ? 'rVbHv^kmN#uaQ0Yk' : runtimeConfig?.public.SOURCE_KEY
  const axios = isWidget ? axiosPlain : axiosAuth

  const simmerringAxios = axios.create({
    baseURL: `${baseUrl}/SimmerringSelector/1.0.0/api`,
    params: {apiKey: simmerringApiKey}
  })

  // Add request interceptor for widget authentication
  if (isWidget && widgetConfig?.companyNo) {
    simmerringAxios.interceptors.request.use((config) => {
      const authHeaders = getWidgetAuthHeaders(
        widgetConfig.companyNo!,
        'simmerring',
        sourceKey!
      )
      config.headers = config.headers || {}
      Object.assign(config.headers, authHeaders)
      return config
    })
  }

  const getSimmerringMetadata = async (): Promise<SimmerringMetadata> => {
    const res = await simmerringAxios.get('/SimmerringFinder')
      .then(response => {
        return response
      })
      .catch(() => {
        return {}
      })
    return res.data
  }

  const postSimmerringFinder = async (
    requestBody: SimmerringFinderRequest
  ): Promise<SimmerringFinderResponse | null> => {
    if (!typeGuard(requestBody)) {return null}

    const res = await simmerringAxios.post('/SimmerringFinder', requestBody)
      .catch(() => ({data: null}))
    return res.data
  }

  const postSimmerringAssistant = async (
    requestBody: SimmerringAssistantRequest
  ): Promise<SimmerringAssistantResponse | null> => {
    if (!typeGuard(requestBody)) {return null}

    const res = await simmerringAxios.post('/SimmerringAssistant', requestBody)
      .catch(() => ({data: null}))
    return res.data
  }

  return {
    getSimmerringMetadata,
    postSimmerringFinder,
    postSimmerringAssistant
  }
}
